import { lazy, ReactNode, Suspense } from "react";
import { RouteObject } from 'react-router-dom';
import PrivateRoute from "./PrivateRoute";
import { Backdrop, CircularProgress } from "@mui/material";
const NotFoundPage = lazy(() => import("../pages/notFoundPage/NotFoundPage"));
const UnauthorizedPage = lazy(() => import("../pages/unAuthorizedPage/UnauthorizedPage"));
const MainLayout = lazy(() => import("../layouts/MainLayout"));
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));
const SettingForm = lazy(() => import("../pages/setting/settingForm/SettingForm"));
const AllSettings = lazy(() => import("../pages/setting/all/AllSettings"));
const ProcedureForm = lazy(() => import("../pages/procedureMaster/procedureForm/ProcedureForm"));
const AllAppointments = lazy(() => import("../pages/appointment/all/AllAppointments"));
const AppointmentCalendar = lazy(() => import("../pages/appointment/appointmentCalendar/AppointmentCalendar"));
const AppointmentCreate = lazy(() => import("../pages/appointment/create/AppointmentCreate"));
const AppointmentPayment = lazy(() => import("../pages/appointment/payment/AppointmentPayment"));
const UpcomingConsultation = lazy(() => import("../pages/consultation/UpcomingConsultation"));
const UpcomingProcedure = lazy(() => import("../pages/procedure/upcoming/UpcomingProcedure"));
const UpcomingFirstProcedure = lazy(() => import("../pages/procedure/upcoming/UpcomingFirstProcedures"));
const ViewSetting = lazy(() => import("../pages/setting/view/ViewSetting"));
const ProcedureCalendar = lazy(() => import("../pages/procedureCalendar/ProcedureCalendar"));
const ConsultationCalendar = lazy(() => import("../pages/consultationCalendar/ConsultationCalendar"));
const AppointmentForm = lazy(() => import("../pages/appointment/appointmentForm/AppointmentForm"));
const AllProceduresMaster = lazy(() => import("../pages/procedureMaster/all/AllProcedures"));
const PackageForm = lazy(() => import("../pages/packageMaster/packageForm/PackageForm"));
const AllPackagesMaster = lazy(() => import("../pages/packageMaster/all/AllPackages"));
const Invoice = lazy(() => import("../pages/invoice"));
const Patients = lazy(() => import("../pages/patients"));
const SinglePatient = lazy(() => import("../pages/patients/SinglePatient"));
const ForceChangePassword = lazy(() => import("../pages/forceChangePassword/ForceChangePassword"));
const BranchForm = lazy(() => import("../pages/branch/branchForm/BranchForm"));
const AllBranches = lazy(() => import("../pages/branch/all/AllBranches"));
const UserForm = lazy(() => import("../pages/user/userForm/UserForm"));
const AllUsers = lazy(() => import("../pages/user/all/AllUsers"));
const Consultation = lazy(() => import('../pages/consultation/Consultation'));
const ConsultationForm = lazy(() => import('../pages/consultation/ConsultationForm'));
const PastConsultation = lazy(() => import('../pages/consultation/PastConsultation'));
const CreateProcedure = lazy(() => import('../pages/consultation/procedure/CreateProcedure'));
const Media = lazy(() => import("../pages/consultation/Media"));
const PaymentHistory = lazy(() => import("../pages/consultation/PaymentHistory"));
const AllProcedures = lazy(() => import("../pages/consultation/procedure/AllProcedure"));
const PastSessions = lazy(() => import("../pages/consultation/procedure/PastSessions"));
const AllPackage = lazy(() => import("../pages/consultation/package/AllPackage"));
const CreatePackage = lazy(() => import("../pages/consultation/package/CreatePackage"));
const SessionForm = lazy(() => import("../pages/consultation/session/SessionForm"));
const NetworkErrorPage = lazy(() => import("../pages/networkErrorPage/NetworkErrorPage"));
const TransactionHistory = lazy(() => import("../pages/transactionHistory"));


export type RouteType = RouteObject;


const Loading = () => (
  <Backdrop
    sx={{
      color: (theme) => theme.palette.primary.contrastText,
      zIndex: (theme) => theme.zIndex.drawer + 1,
    }}
    open={true} // Ensure backdrop is always visible during loading
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

const MainRoutes: RouteType = {
  path: "",
  element:
    <Suspense fallback={<Loading />}>
      <MainLayout />
    </Suspense>
  ,
  children: [
    {
      path: '',
      element:
        <Suspense fallback={<Loading />}>
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        </Suspense>
    },
    {
      path: "consultations",
      children: [
        {
          path: "create/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="create">
                <Consultation action={"create"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              path: "",
              element: <Suspense fallback={<Loading />}><ConsultationForm action="create" permissionKey="consultation" /></Suspense>,
            },
            {
              path: "consultation-tab",
              element: <Suspense fallback={<Loading />}><ConsultationForm action="create" permissionKey="consultation" /></Suspense>,
            },
          ],
        },
        {
          path: "edit/:caseId/:appointmentId/:consultationId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"edit"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              path: "",
              element: <Suspense fallback={<Loading />}><ConsultationForm action="edit" permissionKey="consultation" /></Suspense>,
            },
            {
              path: "consultation-tab",
              element: <Suspense fallback={<Loading />}><ConsultationForm action="edit" permissionKey="consultation" /></Suspense>,
            },
          ],
        },
        {
          path: "past/:caseId/:appointmentId/:consultationId/:page",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="view">
                <PastConsultation />
              </PrivateRoute>
            </Suspense>
          ),
        },
        {
          path: "media/all/:caseId/:appointmentId",
          element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"media"} requiredPermission="view">
            <Media />
          </PrivateRoute>
          </Suspense>,
        },
        {
          path: "payments/all/:caseId/:appointmentId",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"payment"} secondaryKey={"payment-history"} requiredPermission="view">
              <PaymentHistory />
            </PrivateRoute>
          </Suspense>,
        },
      ],
    },
    // Add this to your MainRoutes children array, replacing the existing consultations, procedures, and packages sections

    {
      path: "consultations",
      children: [
        {
          path: "create/:caseId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute >
                <Consultation action={"create"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <Suspense fallback={<Loading />}><ConsultationForm action="create" permissionKey="consultation" /></Suspense>,
            },
            {
              path: "consultation-tab",
              element: <Suspense fallback={<Loading />}><ConsultationForm action="create" permissionKey="consultation" /></Suspense>,
            },
          ],
        },
        {
          path: "create/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute >
                <Consultation action={"create"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <Suspense fallback={<Loading />}><ConsultationForm action="create" permissionKey="consultation" /></Suspense>,
            },
            {
              path: "consultation-tab",
              element: <Suspense fallback={<Loading />}><ConsultationForm action="create" permissionKey="consultation" /></Suspense>,
            },
          ],
        },
        {
          path: "edit/:caseId/:appointmentId/:consultationId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"edit"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: <Suspense fallback={<Loading />}><ConsultationForm action="edit" permissionKey="consultation" /></Suspense>,
            },
            {
              path: "consultation-tab",
              element: <Suspense fallback={<Loading />}><ConsultationForm action="edit" permissionKey="consultation" /></Suspense>,
            },
          ],
        },
        {
          path: "past/:caseId/:appointmentId/:consultationId/:page",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="view">
                <PastConsultation />
              </PrivateRoute>
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "media",
      children: [
        {
          path: "all/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute>
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"media"} requiredPermission="view">
                    <Media />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "payments",
      children: [
        {
          path: "all/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"payment"} secondaryKey={"payment-history"} requiredPermission="view">
                    <PaymentHistory />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "procedures",
      children: [
        {
          path: "create/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"create"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"procedure"} requiredPermission="create">
                    <CreateProcedure action={"create"} requiredPermission="create" />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "all/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"procedure"} requiredPermission="view">
                    <AllProcedures permissionKey={"procedure"} requiredPermission="view" />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "past-sessions/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"procedure"} requiredPermission="view">
                    <PastSessions permissionKey={"procedure"} requiredPermission="view" />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "view/:caseId/:appointmentId/:procedureId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"procedure"} requiredPermission="show">
                    <CreateProcedure permissionKey={"procedure"} requiredPermission="view" action={"view"} />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "edit/:caseId/:appointmentId/:procedureId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"edit"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"procedure"} requiredPermission="edit">
                    <CreateProcedure permissionKey={"procedure"} requiredPermission="edit" action={"edit"} />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "session/view/:caseId/:appointmentId/:sessionId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"session"} requiredPermission="view">
                    <SessionForm permissionKey={"session"} action={"view"} viewOnly={true} />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "session/edit/:caseId/:appointmentId/:sessionId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"edit"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"session"} requiredPermission="edit">
                    <SessionForm permissionKey={"session"} action={"edit"} />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "packages",
      children: [
        {
          path: "create/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"create"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"package"} requiredPermission="create">
                    <CreatePackage />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "all/:caseId/:appointmentId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"package"} requiredPermission="view">
                    <AllPackage permissionKey={"package"} requiredPermission="view" />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "view/:caseId/:appointmentId/:packageId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"view"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"package"} requiredPermission="view">
                    <CreatePackage permissionKey={"package"} requiredPermission="view" action={"view"} />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "edit/:caseId/:appointmentId/:packageId",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"edit"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"package"} requiredPermission="edit">
                    <CreatePackage permissionKey={"package"} requiredPermission="edit" action={"edit"} />
                  </PrivateRoute>
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
    // {
    //   path: "procedures",
    //   children: [
    //     {
    //       path: "create/:caseId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"procedure"} requiredPermission="create">
    //             <CreateProcedure action={"create"} requiredPermission="create" />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "all/:caseId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"procedure"} requiredPermission="view">
    //             <AllProcedures permissionKey={"procedure"} requiredPermission="view" />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "past-sessions/:caseId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"procedure"} requiredPermission="view">
    //             <PastSessions permissionKey={"procedure"} requiredPermission="view" />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "view/:caseId/:procedureId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"procedure"} requiredPermission="show">
    //             <CreateProcedure permissionKey={"procedure"} requiredPermission="view" action={"view"} />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "edit/:caseId/:procedureId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"procedure"} requiredPermission="edit">
    //             <CreateProcedure permissionKey={"procedure"} requiredPermission="edit" action={"edit"} />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "session/view/:caseId/:sessionId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"session"} requiredPermission="view">
    //             <SessionForm permissionKey={"session"} action={"view"} viewOnly={true} />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "session/edit/:caseId/:sessionId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"session"} requiredPermission="edit">
    //             <SessionForm permissionKey={"session"} action={"edit"} />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //   ],
    // },
    // {
    //   path: "packages",
    //   children: [
    //     {
    //       path: "create/:caseId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"package"} requiredPermission="create">
    //             <CreatePackage />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "all/:caseId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"package"} requiredPermission="view">
    //             <AllPackage permissionKey={"package"} requiredPermission="view" />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "view/:caseId/:packageId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"package"} requiredPermission="view">
    //             <CreatePackage permissionKey={"package"} requiredPermission="view" action={"view"} />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //     {
    //       path: "edit/:caseId/:packageId",
    //       element: (
    //         <Suspense fallback={<Loading />}>
    //           <PrivateRoute permissionKey={"package"} requiredPermission="edit">
    //             <CreatePackage permissionKey={"package"} requiredPermission="edit" action={"edit"} />
    //           </PrivateRoute>
    //         </Suspense>
    //       ),
    //     },
    //   ],
    // },
    {
      path: "user",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="create">
                <UserForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:userId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="show">
                <UserForm action={"show"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:userId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="edit">
                <UserForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="view">
                <AllUsers permissionKey={"user"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "branch",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="create">
                <BranchForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:branchId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="view">
                <BranchForm action={"view"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:branchId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="edit">
                <BranchForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="view">
                <AllBranches permissionKey={"branch"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "setting",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="create">
                <SettingForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:settingId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="view">
                <SettingForm action={"view"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:settingId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="edit">
                <SettingForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="view">
                <AllSettings permissionKey={"setting"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view-setting",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="show">
                <ViewSetting />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "procedure",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="create">
                <ProcedureForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:procedureId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="show">
                <ProcedureForm action={"show"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:procedureId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="edit">
                <ProcedureForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="show">
                <AllProceduresMaster permissionKey={"procedure-master"} requiredPermission="show" />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "package",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"master-package"} requiredPermission="create">
                <PackageForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:packageId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"master-package"} requiredPermission="show">
                <PackageForm action={"show"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:packageId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"master-package"} requiredPermission="edit">
                <PackageForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"master-package"} requiredPermission="show">
                <AllPackagesMaster permissionKey={"master-package"} requiredPermission="show" />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "appointment",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"appointment"} requiredPermission="create">
                <AppointmentCreate />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"appointment"} requiredPermission="view">
                <AllAppointments permissionKey={"appointment"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "calendar",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"calendar"} requiredPermission="view">
                <AppointmentCalendar action={"create"} permissionKey={"appointment"} requiredPermission={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:appointmentId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"appointment"} requiredPermission="view">
                <AppointmentCreate action={"view"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:appointmentId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"appointment"} requiredPermission="edit">
                <AppointmentCreate action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "payment/:appointmentId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"payment"} requiredPermission="create">
                <AppointmentPayment />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "upcoming",
      children: [
        {
          path: "consultation/:page",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <UpcomingConsultation permissionKey={"consultation"} requiredPermission="view" />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "consultation/:page/consultation-calendar/:appointmentId",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <ConsultationCalendar />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "procedure/:page",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <UpcomingProcedure permissionKey={"procedure"} requiredPermission="view" />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "first-procedure/:page",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <UpcomingFirstProcedure permissionKey={"procedure"} requiredPermission="view" />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "procedure/:page/procedure-calendar/:dateRange",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <ProcedureCalendar />
            </PrivateRoute>
          </Suspense>
        },
      ]
    },
    {
      path: "invoice/all/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"invoice"} requiredPermission="view">
          <Invoice />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: "transactions/all/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"invoice"} requiredPermission="view">
          <TransactionHistory />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: "patients/all/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"patient"} requiredPermission="view">
          <Patients />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: "patient/:patientId/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"patient"} requiredPermission="view">
          <SinglePatient />
        </PrivateRoute>
      </Suspense>
    },
  ],
};

export default MainRoutes;


export const OtherRoutes: RouteType = {
  path: '',
  children: [
    {
      path: 'unauthorized',
      element: <Suspense fallback={<Loading />}>
        <UnauthorizedPage />
      </Suspense>
    },
    {
      path: 'not-found',
      element: <Suspense fallback={<Loading />}>
        <NotFoundPage />
      </Suspense>
    },
    {
      path: 'network-error',
      element: <Suspense fallback={<Loading />}>
        <NetworkErrorPage />
      </Suspense>
    },
    {
      path: 'change-password',
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute>
          <ForceChangePassword />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: '*',
      element: <Suspense fallback={<Loading />}>
        <NotFoundPage />
      </Suspense>
    },
  ],
}

